import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { getRegulatoryClauses, getComplianceEvidence, seedRegulatoryClauses, downloadCompliancePDF, createComplianceEvidence, updateComplianceEvidence } from '@/services/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Building2, Download, RefreshCw, CheckCircle, AlertCircle, MinusCircle, Eye, Edit2, MoreHorizontal } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const AUTHORITIES = ['gaca', 'easa', 'icao', 'faa'];
const SMS_COMPONENTS = ['policy', 'risk_management', 'assurance', 'promotion'];

const STATUS_COLORS = {
  compliant: 'bg-emerald-900/30 text-emerald-400 border-emerald-900/50',
  partial: 'bg-yellow-900/30 text-yellow-400 border-yellow-900/50',
  non_compliant: 'bg-red-900/30 text-red-400 border-red-900/50',
  not_applicable: 'bg-slate-800 text-slate-400 border-slate-700',
};

const STATUS_ICONS = {
  compliant: CheckCircle,
  partial: MinusCircle,
  non_compliant: AlertCircle,
};

export default function CompliancePage() {
  const { t } = useTranslation();
  const { api, token, user } = useAuth();
  const [clauses, setClauses] = useState([]);
  const [evidence, setEvidence] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAuthority, setSelectedAuthority] = useState('gaca');
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showBatchDialog, setShowBatchDialog] = useState(false);
  const [selectedClause, setSelectedClause] = useState(null);
  const [selectedClauses, setSelectedClauses] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editFormData, setEditFormData] = useState({
    status: '',
    evidence_description: '',
    evidence_references: ''
  });
  const [batchFormData, setBatchFormData] = useState({
    status: 'compliant',
    evidence_description: ''
  });

  useEffect(() => { fetchData(); }, [selectedAuthority]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [clausesRes, evidenceRes] = await Promise.all([
        getRegulatoryClauses(api, { authority: selectedAuthority }),
        getComplianceEvidence(api)
      ]);
      setClauses(clausesRes.data);
      setEvidence(evidenceRes.data);
    } catch (error) { console.error('Failed to fetch compliance data:', error); }
    finally { setLoading(false); }
  };

  const handleSeedClauses = async () => {
    try { await seedRegulatoryClauses(api); fetchData(); }
    catch (error) { console.error('Failed to seed clauses:', error); }
  };

  const handleDownloadPDF = async () => {
    try {
      const response = await downloadCompliancePDF(token, selectedAuthority);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `${selectedAuthority}_compliance.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) { console.error('Failed to download PDF:', error); toast.error('Failed to download PDF'); }
  };

  const handleViewClause = (clause) => {
    setSelectedClause(clause);
    setShowViewDialog(true);
  };

  const handleEditClause = (clause) => {
    const ev = getClauseEvidence(clause.id);
    setSelectedClause(clause);
    setEditFormData({
      status: ev?.status || 'not_assessed',
      evidence_description: ev?.evidence_description || '',
      evidence_references: ev?.evidence_references || ''
    });
    setShowEditDialog(true);
  };

  const handleSaveEvidence = async () => {
    if (!selectedClause) return;
    setIsSubmitting(true);
    
    try {
      const ev = getClauseEvidence(selectedClause.id);
      
      if (ev && ev.id) {
        // Update existing evidence
        await updateComplianceEvidence(api, ev.id, {
          status: editFormData.status,
          evidence_description: editFormData.evidence_description,
          evidence_references: editFormData.evidence_references
        });
        toast.success('Compliance status updated');
      } else {
        // Create new evidence
        await createComplianceEvidence(api, {
          clause_id: selectedClause.id,
          status: editFormData.status,
          evidence_description: editFormData.evidence_description,
          evidence_references: editFormData.evidence_references
        });
        toast.success('Compliance evidence created');
      }
      
      setShowEditDialog(false);
      fetchData();
    } catch (error) {
      console.error('Failed to save evidence:', error);
      toast.error('Failed to save compliance evidence');
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleClauseSelection = (clauseId) => {
    setSelectedClauses(prev => 
      prev.includes(clauseId) 
        ? prev.filter(id => id !== clauseId)
        : [...prev, clauseId]
    );
  };

  const selectAllInComponent = (component) => {
    const componentClauseIds = groupedClauses[component]?.map(c => c.id) || [];
    const allSelected = componentClauseIds.every(id => selectedClauses.includes(id));
    
    if (allSelected) {
      setSelectedClauses(prev => prev.filter(id => !componentClauseIds.includes(id)));
    } else {
      setSelectedClauses(prev => [...new Set([...prev, ...componentClauseIds])]);
    }
  };

  const handleBatchUpdate = async () => {
    if (selectedClauses.length === 0) return;
    setIsSubmitting(true);
    
    try {
      // Process each clause
      for (const clauseId of selectedClauses) {
        const ev = getClauseEvidence(clauseId);
        
        if (ev && ev.id) {
          await updateComplianceEvidence(api, ev.id, {
            status: batchFormData.status,
            evidence_description: batchFormData.evidence_description || ev.evidence_description
          });
        } else {
          await createComplianceEvidence(api, {
            clause_id: clauseId,
            status: batchFormData.status,
            evidence_description: batchFormData.evidence_description
          });
        }
      }
      
      toast.success(`Updated ${selectedClauses.length} clauses successfully`);
      setShowBatchDialog(false);
      setSelectedClauses([]);
      setBatchFormData({ status: 'compliant', evidence_description: '' });
      fetchData();
    } catch (error) {
      console.error('Failed to batch update:', error);
      toast.error('Failed to update some clauses');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getClauseEvidence = (clauseId) => evidence.find(e => e.clause_id === clauseId);

  const groupedClauses = SMS_COMPONENTS.reduce((acc, component) => {
    acc[component] = clauses.filter(c => c.sms_component === component);
    return acc;
  }, {});

  const stats = {
    total: clauses.length,
    compliant: evidence.filter(e => e.status === 'compliant').length,
    partial: evidence.filter(e => e.status === 'partial').length,
    non_compliant: evidence.filter(e => e.status === 'non_compliant').length,
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div><h1 className="text-3xl font-bold text-white font-barlow tracking-tight" data-testid="compliance-title">{t('compliance.title')}</h1>
          <p className="text-slate-400 mt-1">Regulatory compliance mapping and evidence</p>
        </div>
        <div className="flex gap-3">
          {selectedClauses.length > 0 && (
            <Button onClick={() => setShowBatchDialog(true)} className="bg-sky-600 hover:bg-sky-700" data-testid="batch-update-btn">
              <Edit2 className="w-4 h-4 mr-2" />
              Batch Update ({selectedClauses.length})
            </Button>
          )}
          {user?.role === 'admin' && <Button variant="outline" onClick={handleSeedClauses} className="border-slate-700 text-slate-300" data-testid="seed-clauses-btn"><RefreshCw className="w-4 h-4 mr-2" />Seed Clauses</Button>}
          <Button variant="outline" onClick={handleDownloadPDF} className="border-slate-700 text-slate-300" data-testid="download-compliance-btn"><Download className="w-4 h-4 mr-2" />Export PDF</Button>
        </div>
      </div>
      {/* Authority Selector */}
      <div className="flex gap-4 items-center">
        <span className="text-slate-400">Regulatory Authority:</span>
        <Select value={selectedAuthority} onValueChange={setSelectedAuthority}>
          <SelectTrigger className="w-[200px] bg-slate-900 border-slate-800" data-testid="authority-select"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-800">
            {AUTHORITIES.map(a => <SelectItem key={a} value={a}>{t(`compliance.authorities.${a}`)}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="bg-slate-900/60 border-slate-800"><CardContent className="p-4 text-center">
          <p className="text-2xl font-bold text-white">{stats.total}</p><p className="text-slate-400 text-sm">Total Clauses</p>
        </CardContent></Card>
        <Card className="bg-emerald-900/20 border-emerald-900/50"><CardContent className="p-4 text-center">
          <p className="text-2xl font-bold text-emerald-400">{stats.compliant}</p><p className="text-emerald-400/70 text-sm">{t('compliance.statuses.compliant')}</p>
        </CardContent></Card>
        <Card className="bg-yellow-900/20 border-yellow-900/50"><CardContent className="p-4 text-center">
          <p className="text-2xl font-bold text-yellow-400">{stats.partial}</p><p className="text-yellow-400/70 text-sm">{t('compliance.statuses.partial')}</p>
        </CardContent></Card>
        <Card className="bg-red-900/20 border-red-900/50"><CardContent className="p-4 text-center">
          <p className="text-2xl font-bold text-red-400">{stats.non_compliant}</p><p className="text-red-400/70 text-sm">{t('compliance.statuses.non_compliant')}</p>
        </CardContent></Card>
      </div>
      {/* Clauses by SMS Component */}
      <Tabs defaultValue="policy" className="space-y-4">
        <TabsList className="bg-slate-900 border border-slate-800">
          {SMS_COMPONENTS.map(c => <TabsTrigger key={c} value={c} className="data-[state=active]:bg-sky-600 capitalize">{t(`compliance.components.${c}`)}</TabsTrigger>)}
        </TabsList>
        {SMS_COMPONENTS.map(component => (
          <TabsContent key={component} value={component}>
            <Card className="bg-slate-900/60 border-slate-800">
              <CardContent className="p-0">
                {loading ? <div className="flex items-center justify-center py-12"><div className="spinner w-8 h-8" /></div> : groupedClauses[component].length === 0 ? (
                  <div className="text-center py-12 text-slate-500"><Building2 className="w-12 h-12 mx-auto mb-3 opacity-50" /><p>{t('common.noData')}</p></div>
                ) : (
                  <Table>
                    <TableHeader><TableRow className="border-slate-800 hover:bg-transparent">
                      <TableHead className="w-[40px]">
                        <input 
                          type="checkbox"
                          checked={groupedClauses[component]?.every(c => selectedClauses.includes(c.id))}
                          onChange={() => selectAllInComponent(component)}
                          className="w-4 h-4 rounded border-slate-600 bg-slate-800 cursor-pointer"
                        />
                      </TableHead>
                      <TableHead className="text-slate-400">{t('compliance.clauseNumber')}</TableHead><TableHead className="text-slate-400">Title</TableHead><TableHead className="text-slate-400">{t('compliance.regulation')}</TableHead><TableHead className="text-slate-400">{t('common.status')}</TableHead><TableHead className="text-slate-400 w-[100px]">Actions</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {groupedClauses[component].map((clause) => {
                        const ev = getClauseEvidence(clause.id);
                        const StatusIcon = STATUS_ICONS[ev?.status] || MinusCircle;
                        const isSelected = selectedClauses.includes(clause.id);
                        return (
                          <TableRow key={clause.id} className={`border-slate-800 hover:bg-slate-800/50 ${isSelected ? 'bg-sky-900/20' : ''}`}>
                            <TableCell>
                              <input 
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => toggleClauseSelection(clause.id)}
                                className="w-4 h-4 rounded border-slate-600 bg-slate-800 cursor-pointer"
                              />
                            </TableCell>
                            <TableCell className="font-mono text-sky-400">{clause.clause_number}</TableCell>
                            <TableCell className="text-white">{clause.title}</TableCell>
                            <TableCell className="text-slate-300">{clause.regulation}</TableCell>
                            <TableCell>
                              {ev && ev.status ? <Badge className={`${STATUS_COLORS[ev.status]} border flex items-center gap-1 w-fit`}><StatusIcon className="w-3 h-3" />{t(`compliance.statuses.${ev.status}`)}</Badge>
                                : <Badge variant="outline" className="border-slate-700 text-slate-500">Not Assessed</Badge>}
                            </TableCell>
                            <TableCell>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`compliance-actions-${clause.id}`}>
                                    <MoreHorizontal className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="bg-slate-900 border-slate-700">
                                  <DropdownMenuItem onClick={() => handleViewClause(clause)} className="cursor-pointer">
                                    <Eye className="h-4 w-4 mr-2" />
                                    View Details
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleEditClause(clause)} className="cursor-pointer">
                                    <Edit2 className="h-4 w-4 mr-2" />
                                    {ev ? 'Update Status' : 'Assess Compliance'}
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        ))}
      </Tabs>

      {/* View Clause Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Eye className="h-5 w-5 text-sky-400" />
              Clause Details
            </DialogTitle>
          </DialogHeader>
          
          {selectedClause && (
            <div className="space-y-4">
              <div className="p-4 bg-slate-800/50 rounded-lg space-y-3">
                <div className="flex items-center justify-between">
                  <span className="font-mono text-sky-400 font-semibold">{selectedClause.clause_number}</span>
                  <Badge variant="outline" className="uppercase">{selectedAuthority}</Badge>
                </div>
                <h3 className="text-white font-medium">{selectedClause.title}</h3>
                {selectedClause.description && (
                  <p className="text-slate-400 text-sm">{selectedClause.description}</p>
                )}
                <div className="text-sm text-slate-500">
                  <span className="text-slate-400">Regulation:</span> {selectedClause.regulation}
                </div>
              </div>
              
              {/* Current Evidence Status */}
              {(() => {
                const ev = getClauseEvidence(selectedClause.id);
                const StatusIcon = STATUS_ICONS[ev?.status] || MinusCircle;
                return (
                  <div className="p-4 bg-slate-800/30 rounded-lg space-y-2">
                    <Label className="text-slate-400 text-sm">Compliance Status</Label>
                    {ev && ev.status ? (
                      <>
                        <Badge className={`${STATUS_COLORS[ev.status]} border flex items-center gap-1 w-fit`}>
                          <StatusIcon className="w-3 h-3" />
                          {t(`compliance.statuses.${ev.status}`)}
                        </Badge>
                        {ev.evidence_description && (
                          <p className="text-sm text-slate-300 mt-2">{ev.evidence_description}</p>
                        )}
                      </>
                    ) : (
                      <Badge variant="outline" className="border-slate-700 text-slate-500">Not Assessed</Badge>
                    )}
                  </div>
                );
              })()}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowViewDialog(false)} className="border-slate-700">
              Close
            </Button>
            <Button onClick={() => { setShowViewDialog(false); handleEditClause(selectedClause); }} className="bg-sky-600 hover:bg-sky-700">
              <Edit2 className="h-4 w-4 mr-2" />
              Edit Status
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Compliance Evidence Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Edit2 className="h-5 w-5 text-sky-400" />
              Update Compliance Status
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {selectedClause && `${selectedClause.clause_number}: ${selectedClause.title}`}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-slate-300">Compliance Status</Label>
              <Select 
                value={editFormData.status} 
                onValueChange={(v) => setEditFormData(prev => ({ ...prev, status: v }))}
              >
                <SelectTrigger className="bg-slate-800 border-slate-700 mt-1">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700">
                  <SelectItem value="compliant">
                    <span className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-emerald-400" />
                      Compliant
                    </span>
                  </SelectItem>
                  <SelectItem value="partial">
                    <span className="flex items-center gap-2">
                      <MinusCircle className="h-4 w-4 text-yellow-400" />
                      Partially Compliant
                    </span>
                  </SelectItem>
                  <SelectItem value="non_compliant">
                    <span className="flex items-center gap-2">
                      <AlertCircle className="h-4 w-4 text-red-400" />
                      Non-Compliant
                    </span>
                  </SelectItem>
                  <SelectItem value="not_applicable">
                    <span className="flex items-center gap-2">
                      <MinusCircle className="h-4 w-4 text-slate-400" />
                      Not Applicable
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-slate-300">Evidence Description</Label>
              <Textarea
                value={editFormData.evidence_description}
                onChange={(e) => setEditFormData(prev => ({ ...prev, evidence_description: e.target.value }))}
                placeholder="Describe the evidence supporting this compliance status..."
                className="bg-slate-800 border-slate-700 mt-1"
                rows={3}
              />
            </div>
            
            <div>
              <Label className="text-slate-300">Evidence References</Label>
              <Textarea
                value={editFormData.evidence_references}
                onChange={(e) => setEditFormData(prev => ({ ...prev, evidence_references: e.target.value }))}
                placeholder="Document numbers, links, or references to supporting evidence..."
                className="bg-slate-800 border-slate-700 mt-1"
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)} className="border-slate-700">
              Cancel
            </Button>
            <Button 
              onClick={handleSaveEvidence} 
              disabled={isSubmitting || !editFormData.status}
              className="bg-sky-600 hover:bg-sky-700"
            >
              {isSubmitting ? 'Saving...' : 'Save Evidence'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Batch Update Dialog */}
      <Dialog open={showBatchDialog} onOpenChange={setShowBatchDialog}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Edit2 className="h-5 w-5 text-sky-400" />
              Batch Update Compliance Status
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Update {selectedClauses.length} selected clause(s) at once
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="p-3 bg-slate-800/50 rounded-lg border border-slate-700">
              <p className="text-sm text-slate-300">
                <span className="text-sky-400 font-semibold">{selectedClauses.length}</span> clauses selected for batch update
              </p>
            </div>
            
            <div>
              <Label className="text-slate-300">Set Status for All</Label>
              <Select 
                value={batchFormData.status} 
                onValueChange={(v) => setBatchFormData(prev => ({ ...prev, status: v }))}
              >
                <SelectTrigger className="bg-slate-800 border-slate-700 mt-1">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700">
                  <SelectItem value="compliant">
                    <span className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-emerald-400" />
                      Compliant
                    </span>
                  </SelectItem>
                  <SelectItem value="partial">
                    <span className="flex items-center gap-2">
                      <MinusCircle className="h-4 w-4 text-yellow-400" />
                      Partially Compliant
                    </span>
                  </SelectItem>
                  <SelectItem value="non_compliant">
                    <span className="flex items-center gap-2">
                      <AlertCircle className="h-4 w-4 text-red-400" />
                      Non-Compliant
                    </span>
                  </SelectItem>
                  <SelectItem value="not_applicable">
                    <span className="flex items-center gap-2">
                      <MinusCircle className="h-4 w-4 text-slate-400" />
                      Not Applicable
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-slate-300">Evidence Description (Optional)</Label>
              <Textarea
                value={batchFormData.evidence_description}
                onChange={(e) => setBatchFormData(prev => ({ ...prev, evidence_description: e.target.value }))}
                placeholder="Common evidence description for all selected clauses..."
                className="bg-slate-800 border-slate-700 mt-1"
                rows={3}
              />
              <p className="text-xs text-slate-500 mt-1">
                Leave empty to keep existing evidence descriptions
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowBatchDialog(false); setSelectedClauses([]); }} className="border-slate-700">
              Cancel
            </Button>
            <Button 
              onClick={handleBatchUpdate} 
              disabled={isSubmitting}
              className="bg-sky-600 hover:bg-sky-700"
            >
              {isSubmitting ? 'Updating...' : `Update ${selectedClauses.length} Clauses`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
